import { Outlet, createFileRoute } from "@tanstack/react-router";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { useEffect, useMemo } from "react";

import { GrowSidebar } from "@/components/Grow/GrowSidebar";
import { SupportWidget } from "@/components/Generic/SupportWidget";
import { api } from "~/convex/_generated/api";
import { useGrowStore } from "@/state/useGrowStore";
import { useQuery } from "convex/react";

function GrowLayout() {
  // Get store actions and state
  const {
    socialAccounts,
    selectedAccount,
    hasConnectedAccounts,
    setSocialAccounts,
    setSelectedAccount,
  } = useGrowStore();

  // Get current user to fetch their social accounts
  const currentUser = useQuery(api.app.getCurrentUser, {});

  // Extract social accounts from user data (memoized to prevent recreation on every render)
  const extractedSocialAccounts = useMemo(() => {
    const accounts: Array<{
      id: string;
      platform: "instagram" | "youtube" | "tiktok";
      handle: string;
      followers: number;
      posts: number;
    }> = [];

    if (currentUser?.creator && currentUser.creatorSocials) {
      // Add Instagram accounts
      currentUser.creatorSocials.instagram?.forEach((ig, index) => {
        accounts.push({
          id: `ig_${index}`,
          platform: "instagram",
          handle: ig.handle,
          followers: ig.followerCount || 0,
          posts: ig.postCount || 0,
        });
      });

      // Add YouTube accounts
      currentUser.creatorSocials.youtube?.forEach((yt, index) => {
        accounts.push({
          id: `yt_${index}`,
          platform: "youtube",
          handle: yt.handle,
          followers: yt.followerCount || 0,
          posts: yt.postCount || 0,
        });
      });

      // Add TikTok accounts
      currentUser.creatorSocials.tiktok?.forEach((tt, index) => {
        accounts.push({
          id: `tt_${index}`,
          platform: "tiktok",
          handle: tt.handle,
          followers: tt.followerCount || 0,
          posts: tt.postCount || 0,
        });
      });
    }

    return accounts;
  }, [currentUser?.creator, currentUser?.creatorSocials]);

  // Update store when social accounts change
  useEffect(() => {
    setSocialAccounts(extractedSocialAccounts);
  }, [extractedSocialAccounts, setSocialAccounts]);

  return (
    <SidebarProvider>
      <GrowSidebar
        socialAccounts={socialAccounts}
        selectedAccount={selectedAccount}
        onAccountChange={setSelectedAccount}
        hasConnectedAccounts={hasConnectedAccounts}
      />
      <SidebarInset>
        <div className="flex flex-1 flex-col pl-2 pt-2">
          <Outlet />
        </div>
      </SidebarInset>
      <div className="fixed bottom-4 left-14">
        <SupportWidget />
      </div>
    </SidebarProvider>
  );
}

export const Route = createFileRoute("/_app/_auth/grow")({
  component: GrowLayout,
});
